import { Button, InlineLoading, Tooltip } from '@carbon/react';

/** Why Clone Case is disabled with unsaved edits: it copies the saved case, not the form. */
export const CLONE_DIRTY_REASON = 'Save changes before cloning';

/**
 * Clone Case. Disabled while a clone is in flight, and while the form has unsaved edits, which a
 * clone of the saved case would leave behind; the tooltip says why.
 *
 * @param {object} props - The clone state, the form's dirty state and the click handler.
 * @param {boolean} props.cloning - A clone is in flight.
 * @param {boolean} props.isDirty - The form has unsaved edits.
 * @param {Function} props.onClick - Clones the case.
 * @return {JSX.Element} The button.
 */
export function CloneButton({
  cloning,
  isDirty,
  onClick,
}: {
  cloning: boolean;
  isDirty: boolean;
  onClick: () => void;
}) {
  const button = (
    <Button
      kind="tertiary"
      size="md"
      disabled={cloning || isDirty}
      onClick={onClick}
      data-testid="case-clone"
    >
      {cloning ? <InlineLoading /> : 'Clone Case'}
    </Button>
  );

  return isDirty ? (
    // A disabled Button emits no hover, so a focusable span wraps it as the tooltip trigger.
    <Tooltip label={CLONE_DIRTY_REASON} align="bottom">
      <span tabIndex={0}>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}
